import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import {
  PlusCircle,
  Storefront,
  Target,
  ChartBar,
  CalendarDots,
  ArrowLeft,
  ListPlus,
  ListChecks,
  Clock,
  Broom,
  Star,
  IdentificationCard,
} from '@phosphor-icons/react';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useClientPersona } from '@/personas/useClientPersona';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { usePendingStarValues } from '@/components/activity/usePendingStarValues';

const SupervisorHub = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentClient, setClient } = useContext(SupervisorContext);
  // Belöningsadmin gatar på KLIENTENS persona (BACKEND-KONTRAKT p.4), inte på
  // handledarens (som alltid vore 'adult' i handledarläge → stjärnorna dolda).
  const { persona: clientPersona, isFallback, profile } = useClientPersona(currentClient);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  // Saknad/okänd persona → belöning DÖLJS (aldrig barn-default). Handledaren
  // uppmanas i stället bekräfta klientens persona (child/teen/adult) först.
  const starsEnabled = !isFallback && profile.features.stars.enabled;

  // Förslag på stjärnvärden kommer från BÅDA brukarvyerna. Kortet gatades tidigare på
  // clientPersona === 'teen' därför att barnet antogs fastställa sitt eget värde, men
  // den policyn ändrades i V004: både barn och tonåring begär värdet av sin handledare,
  // och backend köar därför även barnets aktiviteter som 'pending'. Med gaten kvar blev
  // barnets förslag osynliga för föräldern - och en aktivitet med ett ogodkänt värde ger
  // noll stjärnor, så barnet kunde göra allt rätt och ändå aldrig få något.
  //
  // Räknaren säger hur många beslut som faktiskt väntar; kön är källan, inte personan.
  const { activities: starValueProposals } = usePendingStarValues();
  const pendingStarValues = starValueProposals.length;
  const starValuesEnabled = starsEnabled;

  const cards: { label: string; icon: React.ReactNode; path: string; show: boolean }[] = [
    {
      label: t('supervisor.hub.createActivity'),
      icon: <PlusCircle size={32} weight="regular" />,
      path: '/create-activity/Title',
      show: true,
    },
    {
      label: t('supervisor.hub.createRoutine'),
      icon: <ListChecks size={32} weight="regular" />,
      path: '/supervisor/routine',
      show: true,
    },
    {
      label: t('supervisor.bulk.title'),
      icon: <ListPlus size={32} weight="regular" />,
      path: '/supervisor/bulk-create',
      show: true,
    },
    {
      label: t('supervisor.hub.shop'),
      icon: <Storefront size={32} weight="regular" />,
      path: '/supervisor/shop',
      show: starsEnabled,
    },
    {
      label:
        pendingStarValues > 0
          ? t('supervisor.hub.starValuesWithCount', { count: pendingStarValues })
          : t('supervisor.hub.starValues'),
      icon: <Star size={32} weight="regular" />,
      path: '/supervisor/star-values',
      show: starValuesEnabled,
    },
    {
      label: t('supervisor.hub.goals'),
      icon: <Target size={32} weight="regular" />,
      path: '/supervisor/goals',
      show: starsEnabled,
    },
    {
      // Profilen (barn/tonåring/vuxen) ägs av handledaren, inte av brukaren. Kortet
      // visas alltid: det är också vägen ur fallback-läget nedan, där belöningsadmin
      // är dold just för att profilen inte är satt.
      label: t('supervisor.hub.persona'),
      icon: <IdentificationCard size={32} weight="regular" />,
      path: '/supervisor/persona',
      show: true,
    },
    {
      label: t('supervisor.hub.timerDisplay'),
      icon: <Clock size={32} weight="regular" />,
      path: '/supervisor/timer-display',
      show: true,
    },
    {
      label: t('supervisor.hub.routineDisplay'),
      icon: <ListChecks size={32} weight="duotone" />,
      path: '/supervisor/routine-display',
      show: true,
    },
    {
      label: t('supervisor.hub.cleanup'),
      icon: <Broom size={32} weight="regular" />,
      path: '/supervisor/cleanup',
      show: true,
    },
    {
      label: t('supervisor.hub.statistics'),
      icon: <ChartBar size={32} weight="regular" />,
      path: '/statistics',
      show: true,
    },
    {
      label: t('supervisor.hub.week'),
      icon: <CalendarDots size={32} weight="regular" />,
      path: '/week',
      show: true,
    },
  ];

  const handleBack = () => {
    setClient(undefined);
    navigate('/supervisor');
  };

  return (
    <Box sx={{ pb: 12 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3, pt: 2 }}>
        <IconButton
          onClick={handleBack}
          aria-label={t('supervisor.hub.back') as string}
          size="small"
        >
          <ArrowLeft size={24} color={tk.text} />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: tk.text }}>
            {currentClient?.name ?? currentClient?.tag ?? ''}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: tk.secondaryBg,
              color: tk.textSoft,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 600,
            }}
          >
            {clientPersona ?? t('supervisor.hub.personaUnset')}
          </Typography>
        </Stack>
      </Stack>

      {/* Fallback: klientens persona är okänd → belöning dold tills den bekräftas.
          Rutan är KLICKBAR och leder till profilvyn: en uppmaning att bekräfta något
          ska leda till stället där det bekräftas, inte bara konstatera bristen. */}
      {isFallback && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => navigate('/supervisor/persona')}
          sx={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            font: 'inherit',
            cursor: 'pointer',
            color: tk.textSoft,
            backgroundColor: tk.card,
            border: `1px solid ${tk.border}`,
            borderRadius: 2,
            p: 1.5,
            mb: 2,
          }}
        >
          {t('supervisor.hub.personaUnsetHint')}
        </Typography>
      )}

      {/* Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        {cards
          .filter((c) => c.show)
          .map((card) => (
            <Box
              key={card.path}
              onClick={() => navigate(card.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(card.path);
                }
              }}
              sx={{
                backgroundColor: tk.card,
                border: `1px solid ${tk.border}`,
                borderRadius: 3,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${tk.border}`,
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              <Box sx={{ color: tk.primary }}>{card.icon}</Box>
              <Typography
                variant="body2"
                fontWeight={600}
                textAlign="center"
                sx={{ color: tk.text }}
              >
                {card.label}
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default SupervisorHub;
